import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../api/client';
import Modal from './Modal';
import CopyButton from './CopyButton';
import ResultIcon from './ResultIcon';

// A backend orderPoller MIN_CONFIRMATIONS értékével kell egyeznie
const REQUIRED_CONFIRMATIONS = 3;

function useCountdown(expiresAt) {
  const [secondsLeft, setSecondsLeft] = useState(() => {
    const diff = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
    return Math.max(0, diff);
  });

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const interval = setInterval(() => {
      setSecondsLeft(s => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [secondsLeft]);

  const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, '0');
  const seconds = String(secondsLeft % 60).padStart(2, '0');
  return { minutes, seconds, expired: secondsLeft === 0 };
}

/** Egy másolható adatsor (összeg, cím, kulcs). */
function Field({ label, value, mono = true, truncate = false }) {
  return (
    <div className="surface-sunken px-3.5 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1">{label}</p>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-gray-100 text-sm ${mono ? 'font-mono' : ''} ${truncate ? 'truncate' : 'break-all'}`}>
          {value}
        </span>
        <CopyButton value={value} variant="icon" />
      </div>
    </div>
  );
}

export default function CryptoPaymentModal({ order, onClose, onExpire, onSuccess }) {
  const [status, setStatus] = useState(order.status || 'pending');
  const [licenseKey, setLicenseKey] = useState(order.license_key || null);
  const [confirmations, setConfirmations] = useState(order.ltc_confirmations || 0);
  const { minutes, seconds, expired } = useCountdown(order.expires_at);

  const isUrgent = parseInt(minutes, 10) < 3;

  useEffect(() => {
    if (expired && status === 'pending' && onExpire) onExpire();
  }, [expired, status, onExpire]);

  useEffect(() => {
    if (status === 'delivered') return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/user/orders/${order.id}`);
        setConfirmations(data.order.ltc_confirmations || 0);
        if (data.order.status === 'delivered') {
          setStatus('delivered');
          setLicenseKey(data.order.license_key);
          if (onSuccess) onSuccess();
        }
      } catch {}
    }, 30000);
    return () => clearInterval(interval);
  }, [order.id, status, onSuccess]);

  const header = {
    title: 'Pay with Litecoin',
    subtitle: `${order.plan.label} — €${order.finalPrice}`,
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M9 8h4.5a2.5 2.5 0 010 5H9v3h4.5a2.5 2.5 0 010 5" />
        <path d="M12 6v2M12 16v2" />
      </svg>
    ),
  };

  // ---- Kifizetve ----
  if (status === 'delivered') {
    return (
      <Modal {...header} onClose={onClose}>
        <div className="text-center pt-2">
          <ResultIcon tone="success" className="mb-5" />
          <p className="font-display text-white font-semibold mb-1">Payment confirmed</p>
          <p className="text-gray-500 text-sm mb-6">
            Your license key is ready. Activate it in your dashboard.
          </p>

          <div className="text-left mb-6">
            <Field label="License key" value={licenseKey} />
          </div>

          <button onClick={onClose} className="btn-primary w-full py-2.5">Go to Dashboard</button>
        </div>
      </Modal>
    );
  }

  // ---- Lejárt ----
  if (expired) {
    return (
      <Modal {...header} onClose={onClose}>
        <div className="text-center pt-2 pb-2">
          <ResultIcon tone="error" className="mb-5" />
          <p className="font-display text-white font-semibold mb-1">Payment expired</p>
          <p className="text-gray-500 text-sm mb-6">
            This payment request has expired. Please start a new order.
          </p>
          <button onClick={onClose} className="btn-secondary w-full py-2.5">Close</button>
        </div>
      </Modal>
    );
  }

  // ---- Fizetésre vár ----
  return (
    <Modal {...header} onClose={onClose}>
      <div className="flex justify-center mb-5">
        <div className="p-3 bg-white rounded-xl shadow-card">
          <QRCodeSVG value={order.ltcAddress} size={160} bgColor="#ffffff" fgColor="#000000" level="M" />
        </div>
      </div>

      <div className="space-y-2.5 mb-5">
        <Field label="Amount (LTC)" value={order.ltcAmount} />
        <Field label="Address" value={order.ltcAddress} truncate />
      </div>

      <div className="flex items-center justify-between gap-3 mb-5 px-3.5 py-2.5
                      rounded-lg bg-brand-500/[0.07] border border-brand-400/20">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="relative flex h-2 w-2 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-400" />
          </span>
          <span className="text-sm text-gray-300 truncate">
            {confirmations > 0
              ? `Confirming — ${confirmations}/${REQUIRED_CONFIRMATIONS}`
              : 'Waiting for payment…'}
          </span>
        </div>
        <span className={`font-mono text-sm font-semibold tabular-nums flex-shrink-0 ${
          isUrgent ? 'text-red-400' : 'text-gray-200'
        }`}>
          {minutes}:{seconds}
        </span>
      </div>

      <p className="text-center text-xs text-gray-600 leading-relaxed">
        Send exactly the amount shown. The payment is detected automatically
        and your key is delivered after {REQUIRED_CONFIRMATIONS} network confirmations.
      </p>
    </Modal>
  );
}
